import { getStatus, STATUS } from './visibility';
import theme from '../styles/theme';

/** @function
 * Converts a skill into its title case equivalent.
 *
 * If the skill is not known, an error is thrown.
 *
 * @param skill {string} - a skill to be converted.
 * @returns {string} The skill's title case equivalent.
 */
export function convertSkillToTitleCase(skill) {
    switch (skill) {
        case 'Entry timing':
            return 'Entry Timing';
        case 'Exit timing':
            return 'Exit Timing';
        case 'Scaling in':
            return 'Scaling In';
        case 'Scaling out':
            return 'Scaling Out';
        case 'Stock picking':
            return 'Stock Picking';
        case 'Sizing':
            return 'Sizing';
        case 'Size adjusting':
            return 'Size Adjusting';
        default:
            throw new Error(`Invalid skill ${skill}`);
    }
}

export function convertSkillToAnalyticsId(skill) {
    switch (skill) {
        case 'Entry timing':
            return 'entry-row';
        case 'Exit timing':
            return 'exit-row';
        case 'Scaling in':
            return 'scaling-in-row';
        case 'Scaling out':
            return 'scaling-out-row';
        case 'Stock picking':
            return 'stock-picking-row';
        case 'Sizing':
            return 'sizing-row';
        case 'Size adjusting':
            return 'size-adjusting-row';
        default:
            throw new Error(`Invalid skill ${skill}`);
    }
}

/**
 * Converts a peer group category in screaming case to a prettified user-friendly one.
 *
 * @param peerCategory {String}
 * @returns {string}
 */
export function screamingPeerCategoryToTitle(peerCategory) {
    if (peerCategory) {
        if (peerCategory === 'ETF') {
            return 'ETF';
        } else {
            return peerCategory
                .split('_')
                .map((word) => word.charAt(0) + word.substring(1).toLowerCase())
                .reduce((acc, word) => (acc ? `${acc} ${word}` : word), '');
        }
    }
}

/**
 * Converts a peer group category in screaming case to kebab-case.
 *
 * @param peerCategory {String}
 * @returns {string}
 */
export function screamingPeerCategoryToKebab(peerCategory) {
    return peerCategory.replaceAll('_', '-').toLowerCase();
}

/**
 * Formats raw benchmark score data objects from API response into objects ready for presentation in the app.
 * The following formatting processes are chained together in a pipe function:
 * 1. Array sorted by baScore
 * 2. Ranking applied based on baScore
 * 3. Visibility status attribute applied based on presence of portfolioLongName/uuid
 * 4. Any ownership data merged into the scores array
 * 5. Dot color applied based on visibility status
 * 6. Victory ordering applied to determine Scatter render order
 *
 * @param {Array<Object>} benchmarkData - array of score objects
 * @param {Array<Object>} ownershipData - array of owned portfolio objects
 * @returns {Array<Object>} - formatted benchmark data array
 */
export const formatFrontierData = (benchmarkData, ownershipData) => {
    const mergeOwnershipDataPartial = (data) =>
        mergeOwnershipData(data, ownershipData);

    return pipe(
        sortByBaScore,
        applyRanking,
        applyStatus,
        mergeOwnershipDataPartial,
        applyDotColor,
        applyVictoryOrdering
    )(benchmarkData);
};

/**
 * Sorts array of portfolio score objects by their baScore attribute, descending.
 * @param {Array<Object>} data - array of score objects
 * @returns {Array<Object>} - sorted array
 */
export const sortByBaScore = (data) =>
    [...data].sort((left, right) => right.baScore - left.baScore);

/**
 * Applies a rank attribute to each object in array in ascending order.
 * @param {Array<Object>} data - array of objects
 * @returns {Array<Object>} - ranked array
 */
export const applyRanking = (data) =>
    data.map((item, index) => ({ ...item, rank: index + 1 }));

/**
 * Applies a status attribute to each score object in array, using the getStatus function to determine status.
 * @param {Array<Object>} data - array of objects
 * @returns {Array<Object>} - array of objects with status applied
 */
export const applyStatus = (data) =>
    data.map((item) => ({ ...item, status: getStatus(item) }));

/**
 * Creates reverse lookup object providing Victory ordering for the given array.
 * Victory ordering is based on the score item's status (hidden < visible < open < owned),
 * and baScore within each status group.
 * @param {Array<Object>} data - array of score objects
 * @returns {Object} - reverse lookup object
 */
const getVictoryOrderings = (data) =>
    Array.from(data.keys())
        .sort((left, right) => {
            return (
                data[left].status - data[right].status ||
                data[left].baScore - data[right].baScore
            );
        })
        .reduce(
            (previousValue, currentValue, currentIndex) => ({
                ...previousValue,
                [currentValue]: currentIndex
            }),
            {}
        );

/**
 * Applies a victoryOrdering attribute to each score object in array.
 * The victoryOrdering value is the order in which the array will be rendered in the Victory Scatter,
 * and is based on the status and baScore attributes (see sortByStatusAndScore).
 * @param {Array<Object>} data - array of score objects
 * @returns {Array<Object>} - array of objects with victoryOrdering applied
 */
export const applyVictoryOrdering = (data) => {
    const victoryOrderings = getVictoryOrderings(data);
    return data.map((item, index) => ({
        ...item,
        victoryOrdering: victoryOrderings[index]
    }));
};

/**
 * Applies a dotColor attribute to each score item in the array based on its status attribute.
 * @param {Array<Object>} data - array of score objects
 * @returns {Array<Object>} - score array with applied dot colors
 */
export const applyDotColor = (data) =>
    data.map((item) => ({
        ...item,
        dotColor: theme.palette.scatterPlot.dotColors[item?.status ?? 0]
    }));

/**
 * Applies a dummy publicToken attribute to each item in array - only to be used for dot to table interaction and not
 * for querying the api. This should only be used for unauthorised score data.
 * @param {Array<Object>} data - array of score objects
 * @returns {Array<Object>} - score array with applied publicToken attributes
 */
export const applyDummyPublicToken = (data) =>
    data.map((item, index) => ({
        ...item,
        publicToken: index
    }));

/**
 * Merges ownership attributes from ownershipData into benchmarkData.
 * Matches are determined by the publicToken attribute common to both arrays.
 * Injected ownership attributes: portfolioLongName, uuid, status.
 * @param {Array<Object>} benchmarkData - array of score objects
 * @param {Array<Object>} ownershipData - array of owned portfolios
 * @returns {Array<Object>} - merged array of scores
 */
export function mergeOwnershipData(benchmarkData, ownershipData) {
    if (!ownershipData) {
        return benchmarkData;
    }

    const ownershipDataMap = new Map(
        ownershipData.map((item) => [item.publicToken, item])
    );

    return benchmarkData.map((item) => {
        const ownershipItem = ownershipDataMap.get(item.publicToken);
        return ownershipItem
            ? {
                  ...item,
                  portfolioLongName: ownershipItem.portfolioLongName,
                  uuid: ownershipItem.privateToken,
                  status: STATUS.owned
              }
            : item;
    });
}

/**
 * Helper function to create a pipeline of chained functions.
 * @param fns - array of functions
 * @returns {function(*): *}
 */
const pipe =
    (...fns) =>
    (x) =>
        fns.reduce((v, f) => f(v), x);

const SKILL_PRECEDENCE = [
    'Stock picking',
    'Sizing',
    'Entry timing',
    'Scaling in',
    'Size adjusting',
    'Scaling out',
    'Exit timing'
];

/** @function
 * Formats the skills data from the api.
 * @param skillsData {Array<Object>} from the useBaScoreBySkillsData hook
 * @param theme {Object}
 * @returns {Array<Object>}
 *  */
export function formatSkillsData(skillsData, theme) {
    if (!skillsData) {
        return [];
    }

    const data = skillsData
        .filter(
            (skillsDot) =>
                SKILL_PRECEDENCE.includes(skillsDot.skill) &&
                Object.values(skillsDot).every((a) => a !== null)
        )
        .map((skillsDot) => {
            const barColor =
                theme.palette.scatterPlot.barColors[skillsDot.skill];
            return { ...skillsDot, barColor };
        })
        .sort((left, right) => {
            const leftIndex = SKILL_PRECEDENCE.indexOf(left.skill);
            const rightIndex = SKILL_PRECEDENCE.indexOf(right.skill);

            return leftIndex - rightIndex;
        });
    return data;
}

/**
 * Gets peer group data for a given ebab period from the timeseries array
 * @param {Array<Object>} timeseriesData - over time data for given portfolio
 * @param {string} ebabPeriod - the ebabPeriod to fetch
 * @returns {null|Object}
 */
export function getPeerGroupData(timeseriesData, ebabPeriod) {
    for (let item of timeseriesData) {
        if (item?.ebabPeriod === ebabPeriod && 'peerGroupMetrics' in item) {
            return item.peerGroupMetrics;
        }
    }
    return null;
}

/**
 * Provides the prop object with appropriate style attributes for an "active" (selected/hovered) dot.
 * @param {Object} props Object to be provided with active dot style
 * @param {Object} theme Global theme of the project containing dot styles
 * @returns {Object} Object with "active" dot style
 */
export function getFrontierActiveDotStyle(props, theme) {
    return {
        style: {
            ...props.style,
            stroke: theme.palette.scatterPlot.frontierPoints.stroke.hover,
            strokeWidth: theme.sizing.scatterPlot.frontierPoints.stroke.enlarged
        },
        size: theme.sizing.scatterPlot.frontierPoints.radius.enlarged
    };
}

/**
 * Provides the prop object with appropriate style attributes for an "inactive" dot.
 * @param {Object} props Object to be provided with "inactive" dot style
 * @param {Object} theme Global theme of the project containing dot styles
 * @param {Boolean} isFaded - Whether to render dot as faded out
 * @returns {Object} Object with "inactive" dot style
 */
export function getFrontierInactiveDotStyle(props, theme, isFaded) {
    return {
        style: {
            ...props.style,
            stroke: theme.palette.scatterPlot.frontierPoints.stroke.initial,
            strokeWidth: theme.sizing.scatterPlot.frontierPoints.stroke.initial,
            opacity: isFaded && 0.3
        },
        size: theme.sizing.scatterPlot.frontierPoints.radius.initial
    };
}

export function getSkillsInactiveDotStyle(props, theme) {
    return {
        style: {
            ...props.style,
            stroke: theme.palette.scatterPlot.skillsPoints.stroke.initial,
            strokeWidth: theme.sizing.scatterPlot.skillsPoints.stroke.initial
        },
        size: theme.sizing.scatterPlot.skillsPoints.radius.initial
    };
}

export function getSkillsActiveDotStyle(props, theme) {
    return {
        style: {
            ...props.style,
            stroke: theme.palette.scatterPlot.skillsPoints.stroke.hover,
            strokeWidth: theme.sizing.scatterPlot.skillsPoints.stroke.enlarged
        },
        size: theme.sizing.scatterPlot.skillsPoints.radius.enlarged
    };
}
